import React, { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { Input } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Alert } from "../../components/ui/Modal";
import { config } from "../../config/env";
import LogoImage from "../../assets/logo.png";
import { authService } from "../../services/auth";
import toast from "react-hot-toast";
import { Ico } from "../../components/ui/Icon";
import { gs } from "../../styles/globalStyles";

const emailSchema = z.object({
  email: z.string().email("Enter a valid email address"),
});
const passwordSchema = z
  .object({
    password: z.string().min(8, "At least 8 characters"),
    password_confirmation: z.string(),
  })
  .refine((d) => d.password === d.password_confirmation, {
    message: "Passwords don't match",
    path: ["password_confirmation"],
  });

type EmailForm = z.infer<typeof emailSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;
type Step = "email" | "otp" | "password" | "done";

// ── Wrap lives OUTSIDE the main component so it never remounts ────────────────
const Wrap: React.FC<{
  subtitle: string;
  logoFailed: boolean;
  onLogoError: () => void;
  children: React.ReactNode;
}> = ({ subtitle, logoFailed, onLogoError, children }) => (
  <div className="min-h-screen bg-card flex items-start sm:items-center justify-center p-4 py-10">
    <div className="fixed top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
    <div className="w-full max-w-md relative">
      <div className="flex flex-col items-center mb-8">
        <div className="mb-4">
          {!logoFailed ? (
            <img
              src={LogoImage}
              alt={config.appName}
              className="w-14 h-14 object-contain"
              onError={onLogoError}
            />
          ) : (
            <Ico name="chart" className="w-14 h-14 text-primary" />
          )}
        </div>
        <h1 className="font-display text-3xl font-bold text-gray-50 tracking-tight">
          {config.appName}
        </h1>
        <p className="text-gray-500 text-sm mt-1">{subtitle}</p>
      </div>
      <div className="bg-gray-900/80 backdrop-blur-md rounded-2xl border border-gray-800/60 p-8 shadow-2xl">
        {children}
      </div>
      <div className="mt-5 text-center">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-primary transition-colors"
        >
          <Ico name="arrow_left" className="w-3.5 h-3.5" /> Back to Sign In
        </Link>
      </div>
    </div>
  </div>
);

// ── OTP Input — uncontrolled so React never fights the user's typing ──────────
const OtpInput: React.FC<{
  onComplete: (code: string) => void;
  onClear: () => void;
  disabled?: boolean;
}> = ({ onComplete, onClear, disabled }) => {
  // Store refs to DOM nodes — no controlled value, no React fighting input
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const focus = (i: number) => refs.current[i]?.focus();

  const readAll = () => refs.current.map((el) => el?.value ?? "").join("");

  const notify = () => {
    const val = readAll();
    if (val.replace(/\D/g, "").length === 6) {
      onComplete(val);
    } else {
      onClear();
    }
  };

  const handleInput = (i: number, e: React.FormEvent<HTMLInputElement>) => {
    const el = e.currentTarget;
    // Keep only the last digit typed
    const digit = el.value.replace(/\D/g, "").slice(-1);
    el.value = digit;
    notify();
    if (digit && i < 5) focus(i + 1);
  };

  const handleKeyDown = (
    i: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const el = refs.current[i];
      if (el) {
        if (el.value) {
          el.value = "";
          notify();
        } else if (i > 0) {
          const prev = refs.current[i - 1];
          if (prev) {
            prev.value = "";
            focus(i - 1);
            notify();
          }
        }
      }
    } else if (e.key === "ArrowLeft" && i > 0) focus(i - 1);
    else if (e.key === "ArrowRight" && i < 5) focus(i + 1);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const digits = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    digits.split("").forEach((d, i) => {
      if (refs.current[i]) refs.current[i]!.value = d;
    });
    notify();
    focus(Math.min(digits.length, 5));
  };

  // When disabled (loading), update visual style without clearing values
  const baseClass = [
    "w-11 h-12 text-center text-gray-50 text-xl font-mono font-bold",
    "rounded-xl border transition-all outline-none",
    "bg-gray-900/80 border-gray-700/50",
    "focus:border-primary/60 focus:bg-gray-900",
    disabled ? "opacity-40 cursor-not-allowed" : "",
  ].join(" ");

  return (
    <div className="flex gap-2 justify-center">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={2}
          disabled={disabled}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          onFocus={(e) => e.target.select()}
          onInput={(e) => handleInput(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          className={baseClass}
        />
      ))}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const ForgotPassword: React.FC = () => {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(""); // set once all 6 filled
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [logoFailed, setLogoFailed] = useState(false);

  const emailForm = useForm<EmailForm>({ resolver: zodResolver(emailSchema) });
  const pwForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  });

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const wrapProps = { logoFailed, onLogoError: () => setLogoFailed(true) };

  const sendOtp = async (data: EmailForm) => {
    setLoading(true);
    setError("");
    try {
      await authService.forgotPassword(data.email);
      setEmail(data.email);
      setStep("otp");
      setCountdown(60);
      toast.success("Code sent! Check your email.");
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Failed to send code. Try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (countdown > 0 || loading) return;
    setLoading(true);
    setError("");
    try {
      await authService.forgotPassword(email);
      setCountdown(60);
      setOtp("");
      toast.success("New code sent!");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to resend code.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    const clean = otp.replace(/\D/g, "");
    if (clean.length < 6) {
      setOtpError("Enter all 6 digits");
      return;
    }
    setLoading(true);
    setOtpError("");
    setError("");
    try {
      await authService.verifyOtp(email, clean);
      setStep("password");
    } catch (err: any) {
      setOtpError(err.response?.data?.message || "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (data: PasswordForm) => {
    setLoading(true);
    setError("");
    try {
      await authService.resetPassword(
        email,
        otp.replace(/\D/g, ""),
        data.password,
        data.password_confirmation,
      );
      setStep("done");
      toast.success("Password reset successfully!");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  if (step === "email")
    return (
      <Wrap {...wrapProps} subtitle="Reset your password">
        <div className="mb-6">
          <h2 className="font-display text-lg font-bold text-gray-50 mb-1">
            Forgot your password?
          </h2>
          <p className="text-gray-500 text-sm">
            Enter your email and we'll send you a 6-digit code.
          </p>
        </div>
        {error && (
          <div className="mb-4">
            <Alert type="error" message={error} />
          </div>
        )}
        <form
          onSubmit={emailForm.handleSubmit(sendOtp)}
          className="flex flex-col gap-4"
        >
          <Input
            label="Email Address"
            type="email"
            placeholder="you@example.com"
            prefix={<Ico name="mail" className="w-4 h-4" />}
            error={emailForm.formState.errors.email?.message}
            autoComplete="email"
            {...emailForm.register("email")}
          />
          <Button type="submit" fullWidth loading={loading} size="lg">
            Send Code
          </Button>
        </form>
      </Wrap>
    );

  if (step === "otp")
    return (
      <Wrap {...wrapProps} subtitle="Enter your verification code">
        <div className="mb-6 text-center">
          <h2 className="font-display text-lg font-bold text-gray-50 mb-1">
            Check your email
          </h2>
          <p className="text-gray-500 text-sm">
            We sent a 6-digit code to{" "}
            <span className="text-primary font-mono text-xs">{email}</span>
          </p>
        </div>

        {(otpError || error) && (
          <div className="mb-4">
            <Alert type="error" message={otpError || error} />
          </div>
        )}

        <div className="flex flex-col gap-5">
          <OtpInput
            onComplete={(code) => {
              setOtp(code);
              setOtpError("");
            }}
            onClear={() => setOtp("")}
            disabled={loading}
          />

          {/* Progress hint */}
          <p className="text-center text-xs text-gray-600">
            {otp.replace(/\D/g, "").length === 6 ? (
              <span className="text-primary">✓ All 6 digits entered</span>
            ) : (
              `${otp.replace(/\D/g, "").length} / 6 digits`
            )}
          </p>

          <Button
            fullWidth
            loading={loading}
            size="lg"
            onClick={verifyOtp}
            disabled={otp.replace(/\D/g, "").length < 6}
          >
            Verify Code
          </Button>

          <div className="text-center">
            {countdown > 0 ? (
              <p className="text-gray-600 text-xs">Resend in {countdown}s</p>
            ) : (
              <button
                type="button"
                onClick={resendOtp}
                disabled={loading}
                className="text-xs text-gray-500 hover:text-primary transition-colors disabled:opacity-40"
              >
                Didn't receive it?{" "}
                <span className="text-primary font-semibold">Resend code</span>
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setStep("email");
              setOtp("");
              setError("");
              setOtpError("");
            }}
            className="text-xs text-gray-600 hover:text-gray-400 text-center transition-colors"
          >
            Use a different email
          </button>
        </div>
      </Wrap>
    );

  if (step === "password")
    return (
      <Wrap {...wrapProps} subtitle="Set your new password">
        <div className="mb-6">
          <h2 className="font-display text-lg font-bold text-gray-50 mb-1">
            New password
          </h2>
          <p className="text-gray-500 text-sm">
            Choose a strong password for your account.
          </p>
        </div>
        {error && (
          <div className="mb-4">
            <Alert type="error" message={error} />
          </div>
        )}
        <form
          onSubmit={pwForm.handleSubmit(resetPassword)}
          className="flex flex-col gap-4"
        >
          <Input
            label="New Password"
            type="password"
            placeholder="Min 8 characters"
            prefix={<Ico name="lock" className="w-4 h-4" />}
            error={pwForm.formState.errors.password?.message}
            autoComplete="new-password"
            {...pwForm.register("password")}
          />
          <Input
            label="Confirm Password"
            type="password"
            placeholder="Repeat password"
            prefix={<Ico name="lock" className="w-4 h-4" />}
            error={pwForm.formState.errors.password_confirmation?.message}
            autoComplete="new-password"
            {...pwForm.register("password_confirmation")}
          />
          <Button type="submit" fullWidth loading={loading} size="lg">
            Reset Password
          </Button>
        </form>
      </Wrap>
    );

  return (
    <Wrap {...wrapProps} subtitle="Password reset complete">
      <div className="flex flex-col items-center text-center gap-5 py-4">
        <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
          <Ico name="check" className="w-7 h-7 text-primary" />
        </div>
        <div>
          <h2 className="font-display text-xl font-bold text-gray-50 mb-2">
            All done!
          </h2>
          <p className="text-gray-400 text-sm">
            Your password has been reset. You can now sign in.
          </p>
        </div>
        <Link to="/login" className={gs.btnPrimary + " w-full justify-center"}>
          Go to Sign In
        </Link>
      </div>
    </Wrap>
  );
};

export default ForgotPassword;
